import { useState, type ReactNode } from 'react'
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { IconButton } from 'wss3-forge'
import { Checkmark16Regular, Copy16Regular } from '@fluentui/react-icons'

/**
 * Defense in depth: while react-markdown v9 already filters `javascript:`,
 * `data:`, `vbscript:` via its built-in defaultUrlTransform, we wrap it with
 * an explicit allowlist (http/https/mailto/tel/#-anchor only) so an upstream
 * dependency change can't silently re-enable a dangerous scheme.
 */
function safeUrl(url: string): string {
  const transformed = defaultUrlTransform(url)
  if (!transformed) return ''
  if (
    /^https?:\/\//i.test(transformed) ||
    /^mailto:/i.test(transformed) ||
    /^tel:/i.test(transformed) ||
    transformed.startsWith('#') ||
    transformed.startsWith('/')
  ) {
    return transformed
  }
  return ''
}

// Inject the markdown stylesheet once globally instead of per message.
// Avoids the N-style-tag leak when long threads render.
if (typeof document !== 'undefined' && !document.getElementById('stark-md-styles')) {
  const styleEl = document.createElement('style')
  styleEl.id = 'stark-md-styles'
  styleEl.textContent = `
    .stark-md p { margin: 0 0 0.5em; line-height: 1.55; }
    .stark-md ul, .stark-md ol { margin: 0 0 0.5em; padding-left: 1.4em; line-height: 1.55; }
    .stark-md li > p:last-child { margin-bottom: 0; }
    .stark-md li.task-list-item { list-style: none; margin-left: -1.2em; }
    .stark-md hr { margin: 0.8em 0; border: 0; border-top: 1px solid var(--border-subtle, #26262a); }
    .stark-md > :first-child { margin-top: 0; }
    .stark-md > :last-child { margin-bottom: 0; }
  `
  document.head.appendChild(styleEl)
}

/**
 * Markdown renderer used inside chat bubbles.
 *
 * Style choices follow Claude-style chat: tight headings, generous code blocks
 * with copy affordance, no marketing-y prose. Links open in a new tab. We rely
 * on react-markdown's defaults for sanitization (it escapes raw HTML).
 */

interface CodeBlockProps {
  children: ReactNode
  text: string
  language: string | undefined
}

function CodeBlock({ children, text, language }: CodeBlockProps) {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1400)
    } catch {
      // clipboard denied — silently ignore (browsers without permission)
    }
  }
  return (
    <div
      style={{
        position: 'relative',
        margin: 'var(--spacing-sm, 8px) 0',
        borderRadius: 'var(--radius-md, 8px)',
        backgroundColor: 'var(--bg-elevated, #161618)',
        border: '1px solid var(--border-subtle, #26262a)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 8px 4px 12px',
          fontSize: 11,
          color: 'var(--text-muted, #8a8a90)',
          backgroundColor: 'var(--bg-subtle, #1c1c20)',
          borderBottom: '1px solid var(--border-subtle, #26262a)',
        }}
      >
        <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {language || 'code'}
        </span>
        <IconButton
          icon={copied ? <Checkmark16Regular /> : <Copy16Regular />}
          tooltip={copied ? 'Copié' : 'Copier'}
          aria-label={copied ? 'Copié' : 'Copier le code'}
          variant="ghost"
          size="sm"
          onClick={onCopy}
        />
      </div>
      <pre
        style={{
          margin: 0,
          padding: 'var(--spacing-md, 12px)',
          overflowX: 'auto',
          fontSize: 13,
          lineHeight: 1.5,
          color: 'var(--text-primary, #e8e8ea)',
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
        }}
      >
        <code>{children}</code>
      </pre>
    </div>
  )
}

const components: Components = {
  // Inline vs fenced: react-markdown v9 no longer passes `inline`. Treat any
  // `code` whose text contains a newline (or carries a `language-*` className)
  // as a fenced block; otherwise it's inline.
  code({ className, children, ...rest }) {
    const text = String(children ?? '')
    const isFenced = !!className?.startsWith('language-') || text.includes('\n')
    if (!isFenced) {
      return (
        <code
          {...rest}
          style={{
            backgroundColor: 'var(--bg-elevated, #1a1a1d)',
            padding: '1px 5px',
            borderRadius: 4,
            fontSize: '0.9em',
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          }}
        >
          {children}
        </code>
      )
    }
    const lang = className?.replace(/^language-/, '')
    const trimmed = text.replace(/\n$/, '')
    return (
      <CodeBlock language={lang} text={trimmed}>
        {trimmed}
      </CodeBlock>
    )
  },
  // The default <pre> wraps our CodeBlock — strip it so we don't get nested boxes.
  pre({ children }) {
    return <>{children}</>
  },
  a({ children, href }) {
    return (
      <a
        href={safeUrl(href ?? '')}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--brand-primary, #6366f1)', textDecoration: 'underline' }}
      >
        {children}
      </a>
    )
  },
  // Heading semantics preserved — we only restyle. Screen readers still see
  // the right level when navigating by heading.
  h1: ({ children }) => <h1 style={{ margin: '0.6em 0 0.3em', fontSize: '1.2em', fontWeight: 600 }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ margin: '0.6em 0 0.3em', fontSize: '1.1em', fontWeight: 600 }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ margin: '0.5em 0 0.25em', fontSize: '1.04em', fontWeight: 600 }}>{children}</h3>,
  h4: ({ children }) => <h4 style={{ margin: '0.5em 0 0.25em', fontSize: '1em', fontWeight: 600 }}>{children}</h4>,
  blockquote({ children }) {
    return (
      <blockquote
        style={{
          margin: '0.5em 0',
          padding: '0.2em 0.8em',
          borderLeft: '3px solid var(--border-subtle, #26262a)',
          color: 'var(--text-muted, #b4b4ba)',
        }}
      >
        {children}
      </blockquote>
    )
  },
  table({ children }) {
    return (
      <div style={{ overflowX: 'auto', margin: '0.5em 0' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: '0.95em',
          }}
        >
          {children}
        </table>
      </div>
    )
  },
  th({ children }) {
    return (
      <th
        style={{
          textAlign: 'left',
          padding: '6px 10px',
          borderBottom: '1px solid var(--border-default, #2e2e34)',
          fontWeight: 600,
        }}
      >
        {children}
      </th>
    )
  },
  td({ children }) {
    return (
      <td
        style={{
          padding: '6px 10px',
          borderBottom: '1px solid var(--border-subtle, #26262a)',
        }}
      >
        {children}
      </td>
    )
  },
}

export interface MarkdownProps {
  source: string
}

export function Markdown({ source }: MarkdownProps) {
  return (
    <div className="stark-md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={safeUrl}
        components={components}
      >
        {source}
      </ReactMarkdown>
    </div>
  )
}
